import { makeApi } from "@zodios/core";
import { z } from "zod";

import { mutableRowAuditSchema, semverSchema } from "@skilitics/nautilus-core-designer-data";

import { featureAccessSchema, userIdsSpecQueryParam, validationErrorSchema } from "../shared.ts";

export const moduleOpenUserSchema = z.object({
  // For convenience when formatting messages, doesn't currently affect functionality
  // as you may
  // Only present when true, currently.
  isYou: z.boolean().optional(),
  email: z.string(),
  given_name: z.string(),
  family_name: z.string(),
});

export type ModuleOpenUser = z.infer<typeof moduleOpenUserSchema>;

// Returned on a 403 when a module is owned by a workspace other than the
// caller's active one. `ownerWorkspace` lets the client offer to switch to it.
export const moduleForbiddenSchema = z.object({
  error: z.string(),
  ownerWorkspace: z.string().nullable(),
});

export type ModuleForbidden = z.infer<typeof moduleForbiddenSchema>;

export const moduleSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  tags: z.array(z.string()),
  learningObjective: z.string().optional().or(z.literal("")),
  defaultLanguage: z.string(),
  // Note: the "must be later than the current version" rule is enforced on edit in InteractModuleForm.
  version: semverSchema,
  // Note: this is currently actually "locked", as in the module menu lock action.
  // It also doesn't actually use the readonly mode in the client, since that's broken,
  // you just get an error and directed back to the modules list.
  // It also has no current connection to the "already open" behavior, which should
  // be able to use readonly as well when that is fixed.
  readonly: z.boolean().optional(),
  topic: z.string().optional().or(z.literal("")),
  discipline: z.string().optional().or(z.literal("")),
  intent: z.array(z.string()),
  focusPrimary: z.string().optional().or(z.literal("")),
  focusAdditional: z.string().optional().or(z.literal("")),
  audience: z.array(z.string()).default([]),
  learnerExperience: z.array(z.string()).default([]),
  thumbnailAssetId: z.string().uuid().or(z.null()),
});

// moduleSchema is for update/create, we don't want version validation on response,
// and audit/ownership/session fields are not part of the update payload.
export const moduleResourceSchema = moduleSchema
  .extend({
    version: z.string(),
    owner: z.string(),
  })
  .merge(mutableRowAuditSchema);

export type InteractModuleResource = z.infer<typeof moduleResourceSchema>;

// alreadyOpenUser is transient session state from DynamoDB — only present on list responses,
// not on the single-module GET.
export const moduleListItemSchema = moduleResourceSchema.extend({
  alreadyOpenUser: moduleOpenUserSchema.nullable(),
});

export type InteractModuleListItem = z.infer<typeof moduleListItemSchema>;

export type UpdateInteractModule = z.infer<typeof moduleSchema>;

export type InteractModuleProperties = UpdateInteractModule; // alias for clarity

export type InteractModuleResourceProperties = InteractModuleProperties &
  Pick<InteractModuleResource, "readonly" | "owner">;

export const moduleActionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("soft-delete") }),
  z.object({ type: z.literal("restore") }),
]);

export type ModuleAction = z.infer<typeof moduleActionSchema>;

const updateModuleSessionOptionsSchema = z.object({
  // should be ~2x poll rate. E.g. if polling every 30s, lock for 60s.
  lockSeconds: z.number().min(30).max(300).optional(),
  // todo: better name? Take over existing session, but currently only for your own sessions
  force: z.boolean().optional(),
});

export type UpdateModuleSessionOptions = z.infer<typeof updateModuleSessionOptionsSchema>;

export const interactModulesApi = makeApi([
  {
    method: "get",
    path: "/interact-modules",
    alias: "getInteractModules",
    parameters: [
      {
        type: "Query",
        name: "createdBy",
        schema: userIdsSpecQueryParam.optional(),
      },
      {
        type: "Query",
        name: "sessionId",
        schema: z.string().optional(),
      },
    ],
    response: z.object({
      items: z.array(moduleListItemSchema),
      createAccess: featureAccessSchema,
      // maybe pagination later...
    }),
  },
  {
    method: "get",
    path: "/interact-modules/:moduleId",
    alias: "getInteractModule",
    parameters: [
      {
        type: "Path",
        name: "moduleId",
        schema: z.string(),
      },
    ],
    response: moduleResourceSchema,
    errors: [
      { status: 403, schema: moduleForbiddenSchema },
      { status: 404, schema: z.string() },
    ],
  },
  {
    method: "post",
    path: "/interact-modules",
    alias: "createInteractModule",
    parameters: [
      {
        type: "Body",
        name: "Module",
        schema: moduleSchema,
      },
    ],
    response: z.void(),
    status: 204,
    errors: [
      {
        status: 400,
        schema: validationErrorSchema,
      },
    ],
  },
  {
    method: "post",
    path: "/interact-modules/:moduleId/sessions/:sessionId",
    alias: "postInteractModuleSession",
    parameters: [
      {
        type: "Path",
        name: "moduleId",
        schema: z.string(),
      },
      {
        type: "Path",
        name: "sessionId",
        schema: z.string(),
      },
      {
        type: "Body",
        name: "options",
        schema: updateModuleSessionOptionsSchema,
      },
    ],
    response: z.void(),
    status: 204,
    errors: [
      {
        // Conflict if someone else already has it open.
        status: 409,
        schema: z.object({
          // Not really for user display but gets logged
          message: z.string().optional(),
          // Slightly overly defensive, we might have other conflict causes later?
          openUser: moduleOpenUserSchema.nullish(),
        }),
      },
    ],
  },
  {
    method: "put",
    path: "/interact-modules/:moduleId",
    alias: "putInteractModule",
    parameters: [
      {
        type: "Path",
        name: "moduleId",
        schema: z.string(),
      },
      {
        type: "Body",
        name: "module",
        schema: moduleSchema,
      },
    ],
    response: z.void(),
    status: 204,
    errors: [
      {
        status: 400,
        schema: validationErrorSchema,
      },
    ],
  },
  {
    method: "delete",
    path: "/interact-modules/:moduleId",
    alias: "purgeInteractModule",
    parameters: [
      {
        type: "Path",
        name: "moduleId",
        schema: z.string(),
      },
    ],
    response: z.void(),
    status: 204,
  },
  {
    method: "post",
    path: "/interact-modules/:moduleId/action",
    alias: "postInteractModuleAction",
    parameters: [
      {
        type: "Path",
        name: "moduleId",
        schema: z.string(),
      },
      {
        type: "Body",
        name: "body",
        schema: z.object({ action: moduleActionSchema }),
      },
    ],
    response: z.void(),
    status: 204,
  },
]);
